import React, { useEffect } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import _get from 'lodash/get';

import { AccordionPanel, Select } from 'reaxl';
import { FilterCheckboxes } from 'reaxl-filters';

import getFilterTitle from '@/utilities/getFilterTitle';

import { srpFiltersDuck } from '@/ducks/srp';

import FilterPlaceholder from './FilterPlaceholder';

const CREW_SIZE_CAB = 'FULLSIZE_CREW+COMPACT_CREW';
const EXTENDED_SIZE_CAB = 'FULLSIZE_EXTEND+COMPACT_EXTEND';
const REGULAR_SIZE_CAB = 'FULLSIZE_REG+COMPACT_REG';

function TruckCabSizeFilter({
    enableDropdown = false,
    onOptionChange = () => {},
    onOptionsClear = () => {},
    handleShowExpansion = () => {},
}) {
    const bodyStyleSubtypeCodeOptions = useSelector(srpFiltersDuck.selectors.getBodyStyleSubtypeCodeOptions);
    const bodyStyleSubtypeCodeValue = useSelector(srpFiltersDuck.selectors.getBodyStyleSubtypeCodeValue);
    const filtersErrors = useSelector(srpFiltersDuck.selectors.getFiltersErrors);

    const filterName = 'bodyStyleSubtypeCode';
    const isExpanded = useSelector((state) => srpFiltersDuck.selectors.isExpansionActive(state, filterName));
    const hasOptions = Array.isArray(bodyStyleSubtypeCodeOptions?.options) && bodyStyleSubtypeCodeOptions.options.length > 0;
    const showFilter = isExpanded && hasOptions;

    useEffect(() => {
        if (bodyStyleSubtypeCodeValue.length) {
            handleShowExpansion(filterName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bodyStyleSubtypeCodeValue.length]);

    if (!hasOptions) return null;

    const title = getFilterTitle('Truck Cab Size', bodyStyleSubtypeCodeValue);
    const filterValues = { bodyStyleSubtypeCode: bodyStyleSubtypeCodeValue };

    // Use find for clarity and efficiency
    const crewCabOption = bodyStyleSubtypeCodeOptions.options.find((option) => option.value === CREW_SIZE_CAB);
    const extendedCabOption = bodyStyleSubtypeCodeOptions.options.find((option) => option.value === EXTENDED_SIZE_CAB);
    const regularCabOption = bodyStyleSubtypeCodeOptions.options.find((option) => option.value === REGULAR_SIZE_CAB);

    const crewPlusExtendedCount = _get(crewCabOption, 'count', 0) + _get(extendedCabOption, 'count', 0);
    const crewPlusExtendedOption = {
        count: crewPlusExtendedCount,
        disabled: crewCabOption?.disabled && extendedCabOption?.disabled,
        value: [CREW_SIZE_CAB, EXTENDED_SIZE_CAB],
    };

    const dropdownOptions = [
        { label: 'Any', value: '0', disabled: false, selected: false, count: null },
        regularCabOption && { ...regularCabOption, label: '3 or less', key: 'regularCab3' },
        extendedCabOption && { ...extendedCabOption, label: '4', key: 'extendedCab4' },
        crewPlusExtendedOption && { ...crewPlusExtendedOption, label: '5', key: 'crewCab5' },
        crewPlusExtendedOption && { ...crewPlusExtendedOption, label: '6', key: 'crewCab6' },
    ].filter(Boolean);

    if (enableDropdown) {
        return (
            <Select
                className="margin-top-3 display-flex justify-content-center"
                layout="horizontal"
                name={filterName}
                options={dropdownOptions}
                onChange={(event) => onOptionChange(event, dropdownOptions)}
                value={filterValues}
                aria-label="TruckCabSeating"
            />
        );
    }

    return (
        <AccordionPanel
            eventKey={filterName}
            data-cmp="truckCabSizeFilter"
            title={title}
            contentPadding={5}
        >
            {!showFilter && <FilterPlaceholder uniqueKey={filterName} />}
            {showFilter && (
                <FilterCheckboxes
                    withAccordion={false}
                    filter={bodyStyleSubtypeCodeOptions}
                    values={filterValues}
                    onOptionChange={onOptionChange}
                    onOptionsClear={onOptionsClear}
                    errors={filtersErrors}
                />
            )}
        </AccordionPanel>
    );
}

TruckCabSizeFilter.propTypes = {
    enableDropdown: PropTypes.bool,
    onOptionChange: PropTypes.func,
    onOptionsClear: PropTypes.func,
    handleShowExpansion: PropTypes.func,
};

export default TruckCabSizeFilter;
